import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import { notificationService } from '../api/services/NotificationService';
import { conversationService } from '../api/services/ConversationService';
import { userService } from '../api/services/UserService';
import {
  startNotificationHub,
  stopNotificationHub,
  onReceiveNotification,
  offReceiveNotification,
  onConversationSummaryUpdated,
  offConversationSummaryUpdated,
  type ConversationSummaryUpdate,
} from '../api/signalr/notificationHub';
import type { RealTimeNotification } from '../api/types/notification';
import type { ConversationViewModel } from '../api/types/conversation/ConversationViewModel';

/**
 * Compact summary shape the message-center dropdown consumes.
 * Derived from `ConversationViewModel` + live push payloads.
 */
export interface ConversationSummary {
  conversationId: string;
  listingId: string;
  listingTitle: string | null;
  peerUserId: string;
  peerDisplayName: string;
  lastMessagePreview: string | null;
  lastMessageAt: string | null;
  unreadMessageCount: number;
}

interface MessageNotificationContextProps {
  messageUnreadCount: number;
  conversationSummaries: ConversationSummary[];
  loading: boolean;
  refresh: () => Promise<void>;
  markConversationRead: (conversationId: string) => Promise<void>;
  /** When set, header unread count excludes this conversation (open on Mesajlar). */
  setBadgeExcludeConversationId: (conversationId: string | null) => void;
}

const MessageNotificationContext = createContext<MessageNotificationContextProps | undefined>(
  undefined,
);

export function useMessageNotifications() {
  const ctx = useContext(MessageNotificationContext);
  if (!ctx) {
    throw new Error(
      'useMessageNotifications yalnızca MessageNotificationProvider içinde kullanılabilir.',
    );
  }
  return ctx;
}

function toSummary(
  vm: ConversationViewModel,
  currentUserId: string,
): ConversationSummary {
  const peerUserId = vm.senderId === currentUserId ? vm.receiverId : vm.senderId;
  return {
    conversationId: vm.id,
    listingId: vm.listingId,
    listingTitle: vm.listingSummary?.title ?? null,
    peerUserId,
    peerDisplayName: 'Kullanıcı',
    lastMessagePreview: vm.lastMessagePreview ?? null,
    lastMessageAt: vm.lastMessageAt ?? null,
    unreadMessageCount: vm.unreadMessageCount ?? 0,
  };
}

function upsertSummary(
  list: ConversationSummary[],
  incoming: ConversationSummary,
): ConversationSummary[] {
  const idx = list.findIndex((s) => s.conversationId === incoming.conversationId);
  const next = idx >= 0 ? [...list] : [incoming, ...list];
  if (idx >= 0) {
    next[idx] = { ...next[idx], ...incoming };
  }
  return next.sort((a, b) => {
    const aT = a.lastMessageAt ? Date.parse(a.lastMessageAt) : 0;
    const bT = b.lastMessageAt ? Date.parse(b.lastMessageAt) : 0;
    return bT - aT;
  });
}

interface ProviderProps {
  children: ReactNode;
}

function formatPeerDisplayName(
  profile: { firstName?: string | null; lastName?: string | null; email?: string | null } | null,
  fallback: string,
): string {
  const firstName = profile?.firstName?.trim();
  if (firstName) return firstName;

  const fullName = [profile?.firstName, profile?.lastName]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ');
  if (fullName) return fullName;

  const email = profile?.email?.trim();
  return email || fallback || 'Kullanıcı';
}

export function MessageNotificationProvider({ children }: ProviderProps) {
  const { isAuthenticated, user } = useAuth();
  const currentUserId = user?.id ?? '';

  const [messageUnreadCount, setMessageUnreadCount] = useState(0);
  const [conversationSummaries, setConversationSummaries] = useState<ConversationSummary[]>([]);
  const [loading, setLoading] = useState(false);

  const hubStarted = useRef(false);
  const peerNameCache = useRef<Record<string, string>>({});
  /** Conversation id the user is viewing on Mesajlar (header badge should omit its unread). */
  const badgeExcludeConversationIdRef = useRef<string | null>(null);

  const hydratePeerDisplayNames = useCallback(async (summaries: ConversationSummary[]) => {
    const missingPeerIds = Array.from(
      new Set(
        summaries
          .map((summary) => summary.peerUserId)
          .filter((peerUserId) => peerUserId && !peerNameCache.current[peerUserId]),
      ),
    );

    if (missingPeerIds.length > 0) {
      const resolvedEntries = await Promise.all(
        missingPeerIds.map(async (peerUserId) => {
          try {
            const result = await userService.getById(peerUserId);
            return [
              peerUserId,
              formatPeerDisplayName(result.data ?? null, peerUserId),
            ] as const;
          } catch {
            return [peerUserId, peerUserId] as const;
          }
        }),
      );

      peerNameCache.current = {
        ...peerNameCache.current,
        ...Object.fromEntries(resolvedEntries),
      };
    }

    return summaries.map((summary) => ({
      ...summary,
      peerDisplayName: peerNameCache.current[summary.peerUserId] ?? summary.peerDisplayName,
    }));
  }, []);

  const getCountParams = useCallback(() => {
    const ex = badgeExcludeConversationIdRef.current;
    return ex ? { excludeConversationId: ex } : undefined;
  }, []);

  const setBadgeExcludeConversationId = useCallback(
    (conversationId: string | null) => {
      badgeExcludeConversationIdRef.current = conversationId;
    },
    [],
  );

  const refresh = useCallback(async () => {
    if (!currentUserId) return;
    setLoading(true);
    try {
      const [countRes, convRes] = await Promise.all([
        conversationService.getUnreadMessageCount(getCountParams()).catch(() => null),
        conversationService.getMyConversations({
          pageNumber: 1,
          pageSize: 100,
          withUnread: true,
        }).catch(() => null),
      ]);
      if (countRes?.isSuccess && typeof countRes.data === 'number') {
        setMessageUnreadCount(countRes.data);
      }
      if (convRes?.isSuccess && convRes.data) {
        const summaries = await hydratePeerDisplayNames(
          convRes.data
          .map((c) => toSummary(c, currentUserId))
          .sort((a, b) => {
            const aT = a.lastMessageAt ? Date.parse(a.lastMessageAt) : 0;
            const bT = b.lastMessageAt ? Date.parse(b.lastMessageAt) : 0;
            return bT - aT;
          }),
        );
        setConversationSummaries(summaries);
        if (!(countRes?.isSuccess && typeof countRes.data === 'number')) {
          setMessageUnreadCount(
            summaries.reduce((sum, summary) => sum + summary.unreadMessageCount, 0),
          );
        }
      }
    } catch {
      /* stale data remains visible – non-critical */
    } finally {
      setLoading(false);
    }
  }, [currentUserId, hydratePeerDisplayNames, getCountParams]);

  const resolvePeerDisplayName = useCallback(async (peerUserId: string) => {
    if (!peerUserId || peerNameCache.current[peerUserId]) return;

    try {
      const result = await userService.getById(peerUserId);
      const peerDisplayName = formatPeerDisplayName(result.data ?? null, peerUserId);
      peerNameCache.current = {
        ...peerNameCache.current,
        [peerUserId]: peerDisplayName,
      };
      setConversationSummaries((prev) =>
        prev.map((summary) =>
          summary.peerUserId === peerUserId ? { ...summary, peerDisplayName } : summary,
        ),
      );
    } catch {
      peerNameCache.current = {
        ...peerNameCache.current,
        [peerUserId]: peerUserId,
      };
    }
  }, []);

  const handleRealTimeNotification = useCallback((rt: RealTimeNotification) => {
    if (rt.category !== 'Message') return;
  }, []);

  const handleConversationSummaryUpdated = useCallback(
    (update: ConversationSummaryUpdate) => {
      if (!currentUserId) return;
      const peerUserId =
        update.participantUserIds.find((id) => id !== currentUserId) ?? '';
      const peerDisplayName = peerNameCache.current[peerUserId] ?? 'Kullanıcı';
      const openExcluded = badgeExcludeConversationIdRef.current;

      let previousUnreadForDelta = 0;
      setConversationSummaries((prev) => {
        previousUnreadForDelta =
          prev.find((s) => s.conversationId === update.conversationId)?.unreadMessageCount ?? 0;
        return upsertSummary(prev, {
          conversationId: update.conversationId,
          listingId: update.listingId,
          listingTitle: update.listingTitle,
          peerUserId,
          peerDisplayName,
          lastMessagePreview: update.lastMessagePreview,
          lastMessageAt: update.lastMessageAt,
          unreadMessageCount: update.unreadMessageCount,
        });
      });

      if (update.conversationId === openExcluded) {
        void (async () => {
          const r = await conversationService.getUnreadMessageCount(
            openExcluded ? { excludeConversationId: openExcluded } : undefined,
          );
          if (r.isSuccess && typeof r.data === 'number') {
            setMessageUnreadCount(r.data);
          }
        })();
      } else {
        const delta = update.unreadMessageCount - previousUnreadForDelta;
        if (delta !== 0) {
          setMessageUnreadCount((c) => Math.max(0, c + delta));
        }
      }

      if (peerUserId && !peerNameCache.current[peerUserId]) {
        void resolvePeerDisplayName(peerUserId);
      }
    },
    [currentUserId, resolvePeerDisplayName],
  );

  useEffect(() => {
    if (!isAuthenticated || !currentUserId) {
      setMessageUnreadCount(0);
      setConversationSummaries([]);
      peerNameCache.current = {};
      badgeExcludeConversationIdRef.current = null;
      if (hubStarted.current) {
        stopNotificationHub();
        hubStarted.current = false;
      }
      return;
    }

    void refresh();

    let cancelled = false;
    async function connectHub() {
      try {
        onReceiveNotification(handleRealTimeNotification);
        onConversationSummaryUpdated(handleConversationSummaryUpdated);
        await startNotificationHub();
        if (!cancelled) hubStarted.current = true;
      } catch (err) {
        console.error('Message notification hub connection failed:', err);
      }
    }
    void connectHub();

    return () => {
      cancelled = true;
      offReceiveNotification(handleRealTimeNotification);
      offConversationSummaryUpdated(handleConversationSummaryUpdated);
    };
  }, [
    isAuthenticated,
    currentUserId,
    refresh,
    handleRealTimeNotification,
    handleConversationSummaryUpdated,
  ]);

  const markConversationRead = useCallback(async (conversationId: string) => {
    let delta = 0;
    setConversationSummaries((prev) => {
      const match = prev.find((s) => s.conversationId === conversationId);
      delta = match?.unreadMessageCount ?? 0;
      if (delta === 0) return prev;
      return prev.map((s) =>
        s.conversationId === conversationId ? { ...s, unreadMessageCount: 0 } : s,
      );
    });
    if (delta > 0) {
      setMessageUnreadCount((c) => Math.max(0, c - delta));
    }
    try {
      await Promise.all([
        notificationService.markReadForConversation(conversationId),
        conversationService.markMessagesRead(conversationId),
      ]);
    } catch {
      /* best-effort – next refresh will reconcile */
    }
    const r = await conversationService.getUnreadMessageCount(getCountParams());
    if (r.isSuccess && typeof r.data === 'number') {
      setMessageUnreadCount(r.data);
    }
  }, [getCountParams]);

  const value: MessageNotificationContextProps = {
    messageUnreadCount,
    conversationSummaries,
    loading,
    refresh,
    markConversationRead,
    setBadgeExcludeConversationId,
  };

  return (
    <MessageNotificationContext.Provider value={value}>
      {children}
    </MessageNotificationContext.Provider>
  );
}

export default MessageNotificationContext;
